import React, { useState, useEffect } from 'react';
import inventoryService from '../services/inventoryService';
import toast from 'react-hot-toast';
import ConfirmModal from './ConfirmModal';
import {
  Plus, Trash2, X, Edit2, Search, CheckSquare, Square,
  Ruler, Power, PowerOff
} from 'lucide-react';

const UnitsManager = () => {
  const [loading, setLoading] = useState(false);
  const [units, setUnits] = useState([]);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [selectedUnits, setSelectedUnits] = useState([]);
  const [showInactive, setShowInactive] = useState(false);

  // Confirm modal state
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    type: 'warning',
    title: '',
    message: '',
    onConfirm: null
  });

  // Form state
  const [formData, setFormData] = useState({
    nome: '',
    sigla: '',
    descricao: '',
    tipo: 'UNIDADE',
    fator_conversao: 1,
    ativo: true
  });

  // Tipos de unidade disponíveis
  const tiposUnidade = [
    { value: 'UNIDADE', label: 'Unidade (peças, itens)' },
    { value: 'PESO', label: 'Peso (kg, g, t)' },
    { value: 'VOLUME', label: 'Volume (L, m³)' },
    { value: 'COMPRIMENTO', label: 'Comprimento (m, cm, mm)' },
    { value: 'AREA', label: 'Área (m²)' },
    { value: 'TEMPO', label: 'Tempo (h, min)' },
    { value: 'EMBALAGEM', label: 'Embalagem (caixa, pacote)' },
    { value: 'OUTROS', label: 'Outros' }
  ];

  useEffect(() => {
    loadUnits();
  }, []);

  const loadUnits = async () => {
    setLoading(true);
    try {
      const response = await inventoryService.getUnits();
      if (response.success) {
        setUnits(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar unidades:', error);
      toast.error('Erro ao carregar unidades');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadUnits();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.nome) {
      toast.error('Nome da unidade é obrigatório');
      return;
    }

    if (!formData.sigla) {
      toast.error('Sigla da unidade é obrigatória');
      return;
    }

    try {
      if (editingUnit) {
        // Atualizar
        const data = {
          nome: formData.nome,
          descricao: formData.descricao,
          tipo: formData.tipo,
          fator_conversao: formData.fator_conversao,
          ativo: formData.ativo
        };

        await inventoryService.updateUnit(editingUnit.id, data);
        toast.success('Unidade atualizada com sucesso!');
      } else {
        // Criar
        await inventoryService.createUnit(formData);
        toast.success('Unidade criada com sucesso!');
      }

      resetForm();
      loadUnits();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao salvar unidade');
    }
  };

  const handleEdit = (unit) => {
    setEditingUnit(unit);
    setFormData({
      nome: unit.nome || '',
      sigla: unit.sigla || '',
      descricao: unit.descricao || '',
      tipo: unit.tipo || 'UNIDADE',
      fator_conversao: unit.fator_conversao || 1,
      ativo: unit.ativo
    });
    setShowForm(true);
  };

  // Inativar unidade
  const handleInactivate = async (id) => {
    setConfirmModal({
      show: true,
      type: 'warning',
      title: 'Inativar Unidade',
      message: 'Tem certeza que deseja inativar esta unidade?',
      onConfirm: async () => {
        try {
          await inventoryService.deleteUnit(id);
          toast.success('Unidade inativada com sucesso!');
          loadUnits();
        } catch (error) {
          toast.error(error.response?.data?.message || 'Erro ao inativar unidade');
        }
      }
    });
  };

  // Ativar unidade
  const handleActivate = async (id) => {
    try {
      await inventoryService.updateUnit(id, { ativo: true });
      toast.success('Unidade ativada com sucesso!');
      loadUnits();
    } catch (error) {
      toast.error('Erro ao ativar unidade');
    }
  };

  // Excluir definitivamente
  const handleDelete = async (id) => {
    setConfirmModal({
      show: true,
      type: 'danger',
      title: 'Excluir Unidade',
      message: 'ATENÇÃO: Exclusão definitiva! A unidade será removida permanentemente. Continuar?',
      onConfirm: async () => {
        try {
          await inventoryService.deleteUnit(id);
          toast.success('Unidade excluída com sucesso!');
          loadUnits();
        } catch (error) {
          const msg = error.response?.data?.message || 'Erro ao excluir unidade';
          toast.error(msg);
        }
      }
    });
  };

  // Seleção individual
  const toggleSelectUnit = (id) => {
    setSelectedUnits(prev =>
      prev.includes(id)
        ? prev.filter(u => u !== id)
        : [...prev, id]
    );
  };

  // Seleção todos
  const toggleSelectAll = () => {
    const filteredUnits = filteredUnitsList;
    if (selectedUnits.length === filteredUnits.length) {
      setSelectedUnits([]);
    } else {
      setSelectedUnits(filteredUnits.map(u => u.id));
    }
  };

  // Inativar selecionados
  const handleInactivateSelected = async () => {
    if (selectedUnits.length === 0) {
      toast.error('Nenhuma unidade selecionada');
      return;
    }

    setConfirmModal({
      show: true,
      type: 'warning',
      title: 'Inativar Unidades',
      message: `Tem certeza que deseja inativar ${selectedUnits.length} unidade(s)?`,
      onConfirm: async () => {
        try {
          setLoading(true);
          for (const id of selectedUnits) {
            await inventoryService.deleteUnit(id);
          }
          toast.success(`${selectedUnits.length} unidade(s) inativada(s) com sucesso!`);
          setSelectedUnits([]);
          loadUnits();
        } catch (error) {
          toast.error('Erro ao inativar unidades');
        } finally {
          setLoading(false);
        }
      }
    });
  };

  // Filtrar unidades
  const filteredUnitsList = units.filter(unit => {
    const matchesSearch = !search ||
      unit.nome.toLowerCase().includes(search.toLowerCase()) ||
      (unit.sigla && unit.sigla.toLowerCase().includes(search.toLowerCase())) ||
      (unit.descricao && unit.descricao.toLowerCase().includes(search.toLowerCase()));

    if (showInactive) {
      return matchesSearch;
    }
    return matchesSearch && unit.ativo;
  });

  const resetForm = () => {
    setShowForm(false);
    setEditingUnit(null);
    setFormData({
      nome: '',
      sigla: '',
      descricao: '',
      tipo: 'UNIDADE',
      fator_conversao: 1,
      ativo: true
    });
  };

  // Obter label do tipo
  const getTipoLabel = (tipo) => {
    const found = tiposUnidade.find(t => t.value === tipo);
    return found ? found.label : tipo;
  };

  // Obter cor do tipo para badge
  const getTipoColor = (tipo) => {
    const colors = {
      'UNIDADE': 'bg-blue-100 text-blue-800',
      'PESO': 'bg-green-100 text-green-800',
      'VOLUME': 'bg-cyan-100 text-cyan-800',
      'COMPRIMENTO': 'bg-purple-100 text-purple-800',
      'AREA': 'bg-yellow-100 text-yellow-800',
      'TEMPO': 'bg-orange-100 text-orange-800',
      'EMBALAGEM': 'bg-pink-100 text-pink-800',
      'OUTROS': 'bg-gray-100 text-gray-800'
    };
    return colors[tipo] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Unidades de Medida</h1>
          <p className="text-gray-600">Gerencie as unidades de medida do sistema</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          Nova Unidade
        </button>
      </div>

      {/* Search and Actions */}
      <div className="mb-6 flex gap-4 flex-wrap">
        <form onSubmit={handleSearch} className="flex-1 flex gap-4 min-w-[300px]">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Buscar por nome, sigla ou descrição..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <button type="submit" className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700">
            Buscar
          </button>
        </form>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="w-4 h-4 text-blue-600 border-gray-300 rounded"
          />
          <span className="text-sm text-gray-700">Mostrar inativas</span>
        </label>

        {selectedUnits.length > 0 && (
          <button
            onClick={handleInactivateSelected}
            className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
          >
            <PowerOff className="w-4 h-4" />
            Inativar ({selectedUnits.length})
          </button>
        )}
      </div>

      {/* Units List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={toggleSelectAll}
                  className="text-gray-500 hover:text-gray-700"
                  title={selectedUnits.length === filteredUnitsList.length ? "Desmarcar todos" : "Selecionar todos"}
                >
                  {selectedUnits.length === filteredUnitsList.length && filteredUnitsList.length > 0 ? (
                    <CheckSquare className="w-5 h-5" />
                  ) : (
                    <Square className="w-5 h-5" />
                  )}
                </button>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Sigla
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Nome
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Tipo
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Descrição
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Fator Conversão
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan="8" className="px-6 py-4 text-center text-gray-500">
                  Carregando...
                </td>
              </tr>
            ) : filteredUnitsList.length === 0 ? (
              <tr>
                <td colSpan="8" className="px-6 py-4 text-center text-gray-500">
                  Nenhuma unidade encontrada
                </td>
              </tr>
            ) : (
              filteredUnitsList.map((unit) => (
                <tr key={unit.id} className={`hover:bg-gray-50 ${selectedUnits.includes(unit.id) ? 'bg-blue-50' : ''} ${!unit.ativo ? 'bg-gray-100' : ''}`}>
                  <td className="px-4 py-4">
                    <button
                      onClick={() => toggleSelectUnit(unit.id)}
                      className="text-gray-500 hover:text-blue-600"
                    >
                      {selectedUnits.includes(unit.id) ? (
                        <CheckSquare className="w-5 h-5 text-blue-600" />
                      ) : (
                        <Square className="w-5 h-5" />
                      )}
                    </button>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <Ruler className="w-5 h-5 text-gray-400 mr-3" />
                      <span className="text-sm font-medium text-gray-900">{unit.sigla}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {unit.nome}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${getTipoColor(unit.tipo)}`}>
                      {getTipoLabel(unit.tipo)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {unit.descricao || '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {unit.fator_conversao || 1}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      unit.ativo
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-200 text-gray-600'
                    }`}>
                      {unit.ativo ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      {unit.ativo ? (
                        <button
                          onClick={() => handleInactivate(unit.id)}
                          className="text-orange-600 hover:text-orange-800 p-1"
                          title="Inativar"
                        >
                          <PowerOff className="w-4 h-4" />
                        </button>
                      ) : (
                        <button
                          onClick={() => handleActivate(unit.id)}
                          className="text-green-600 hover:text-green-800 p-1"
                          title="Ativar"
                        >
                          <Power className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleEdit(unit)}
                        className="text-blue-600 hover:text-blue-800 p-1"
                        title="Editar"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(unit.id)}
                        className="text-red-600 hover:text-red-800 p-1"
                        title="Excluir definitivamente"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-semibold">
                {editingUnit ? 'Editar Unidade' : 'Nova Unidade'}
              </h2>
              <button onClick={resetForm} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sigla *
                </label>
                <input
                  type="text"
                  value={formData.sigla}
                  onChange={(e) => setFormData({ ...formData, sigla: e.target.value.toUpperCase() })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: UN, KG, M, CX"
                  maxLength={10}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Código curto usado no sistema (ex: UN, KG, M)</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome *
                </label>
                <input
                  type="text"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: Unidade, Quilograma, Metro"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo de Unidade
                </label>
                <select
                  value={formData.tipo}
                  onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  {tiposUnidade.map((tipo) => (
                    <option key={tipo.value} value={tipo.value}>{tipo.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição
                </label>
                <textarea
                  value={formData.descricao}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  rows={2}
                  placeholder="Descrição adicional da unidade..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fator de Conversão
                </label>
                <input
                  type="number"
                  step="0.001"
                  min="0"
                  value={formData.fator_conversao}
                  onChange={(e) => setFormData({ ...formData, fator_conversao: parseFloat(e.target.value) || 1 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="1"
                />
                <p className="text-xs text-gray-500 mt-1">Fator para conversão entre unidades (padrão: 1)</p>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="ativo"
                  checked={formData.ativo}
                  onChange={(e) => setFormData({ ...formData, ativo: e.target.checked })}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="ativo" className="ml-2 text-sm text-gray-700">
                  Unidade ativa
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingUnit ? 'Atualizar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      <ConfirmModal
        show={confirmModal.show}
        type={confirmModal.type}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={() => {
          if (confirmModal.onConfirm) {
            confirmModal.onConfirm();
          }
          setConfirmModal(prev => ({ ...prev, show: false }));
        }}
        onCancel={() => setConfirmModal(prev => ({ ...prev, show: false }))}
      />
    </div>
  );
};

export default UnitsManager;
